import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import adminAPI from "./apiClient";
import type { LLMRequest } from "./types";
import { getTotalCostFromApi } from "./modelPricing";
import { formatDateWithZone } from "../../utils/formatDate";

type TabId = "prompt" | "response" | "analysis";

interface CalculatedStats {
  promptTokens: number;
  responseTokens: number;
  totalTokens: number;
  processingTime: number;
  cost: number;
  tokensPerSecond: number;
}

export default function LLMRequestDetail(): JSX.Element {
  const { conversationId, turnId, actionId, llmRequestId } = useParams<{ conversationId: string; turnId: string; actionId: string; llmRequestId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromTab = searchParams.get("fromTab") || "actions";
  const [llmRequest, setLlmRequest] = useState<LLMRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabId>("prompt");

  useEffect(() => {
    fetchLLMRequestDetails();
  }, [llmRequestId]);

  const fetchLLMRequestDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      const llmRequestData = await adminAPI.getLLMRequest(conversationId, llmRequestId);
      setLlmRequest(llmRequestData);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
    } finally {
      setLoading(false);
    }
  };

  const formatJSON = (data: unknown): string => {
    try {
      if (typeof data === 'string') {
        return JSON.stringify(JSON.parse(data), null, 2);
      }
      return JSON.stringify(data, null, 2);
    } catch {
      return String(data);
    }
  };

  const parsePromptContent = (prompt: string): string => {
    try {
      const parsed = JSON.parse(prompt);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed
          .map((msg: { role?: string; content?: string }) => {
            const role = msg.role ? `[${msg.role.toUpperCase()}]` : '[UNKNOWN]';
            return `${role}\n${msg.content || ''}`;
          })
          .join('\n\n---\n\n');
      }
      return prompt;
    } catch {
      return prompt;
    }
  };

  const copyToClipboard = async (text: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(text);
      // You could add a toast notification here if desired
    } catch (err) {
    }
  };

  const calculateStats = (): CalculatedStats => {
    if (!llmRequest) {
      return {
        promptTokens: 0,
        responseTokens: 0,
        totalTokens: 0,
        processingTime: 0,
        cost: 0,
        tokensPerSecond: 0
      };
    }

    const promptTokens = llmRequest.prompt_tokens || 0;
    const responseTokens = llmRequest.completion_tokens || 0;
    const totalTokens = promptTokens + responseTokens;
    const processingTime = llmRequest.total_time_ms || 0;
    // Use the API-provided cost from the database (calculated at time of LLM request)
    const cost = getTotalCostFromApi(llmRequest.input_cost_usd, llmRequest.output_cost_usd) || 0;

    return {
      promptTokens,
      responseTokens,
      totalTokens,
      processingTime,
      cost,
      tokensPerSecond: processingTime > 0 ? Math.round((totalTokens / processingTime) * 1000) : 0
    };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!llmRequest) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-500">LLM request not found.</div>
      </div>
    );
  }

  const stats = calculateStats();

  const handleBackToAction = () => {
    navigate(`/admin/conversation/${conversationId}/turn/${turnId}/action/${actionId}?fromTab=${fromTab}`);
  };

  return (
    <div className="space-y-6">
      {/* LLM Request Header */}
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <button
              onClick={handleBackToAction}
              className="text-gray-500 hover:text-gray-700 mr-2"
              title="Back to Action"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h2 className="text-2xl font-bold text-gray-900">LLM Request Details</h2>
          </div>
          {error && (
            <div className="text-sm text-amber-600 bg-amber-50 px-3 py-1 rounded-md">
              Using mock data - API not available
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-500">Request ID</label>
            <p className="mt-1 text-sm font-mono text-gray-900">{llmRequest.llm_request_id}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500">Model</label>
            <p className="mt-1 text-sm text-gray-900">{llmRequest.model}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500">Created At</label>
            <p className="mt-1 text-sm text-gray-900">{formatDateWithZone(llmRequest.created_at)}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500">Processing Time</label>
            <p className="mt-1 text-sm text-gray-900">
              {stats.processingTime ? `${stats.processingTime}ms` : 'N/A'}
            </p>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {[
              { id: "prompt", name: "Prompt" },
              { id: "response", name: "Response" },
              { id: "analysis", name: "Analysis" }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as TabId)}
                className={`py-4 px-6 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {/* Prompt Tab */}
          {activeTab === "prompt" && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-lg font-medium text-gray-900">Full Prompt</h4>
                <div className="flex items-center gap-4">
                  <div className="text-sm text-gray-500">
                    {stats.promptTokens} tokens
                  </div>
                  <button
                    onClick={() => copyToClipboard(parsePromptContent(llmRequest.prompt))}
                    className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                    title="Copy prompt to clipboard"
                  >
                    Copy
                  </button>
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-md max-h-96 overflow-y-auto">
                <pre className="text-sm text-gray-900 whitespace-pre-wrap font-sans">
                  {parsePromptContent(llmRequest.prompt)}
                </pre>
              </div>
            </div>
          )}

          {/* Response Tab */}
          {activeTab === "response" && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-lg font-medium text-gray-900">Full Response</h4>
                <div className="text-sm text-gray-500">
                  {stats.responseTokens} tokens
                </div>
              </div>
              <div className="bg-blue-50 p-4 rounded-md max-h-96 overflow-y-auto">
                <pre className="text-sm text-gray-900 whitespace-pre-wrap">
                  {formatJSON(llmRequest.response)}
                </pre>
              </div>
            </div>
          )}

          {/* Analysis Tab */}
          {activeTab === "analysis" && (
            <div className="space-y-6">
              <div>
                <h4 className="text-lg font-medium text-gray-900 mb-3">Request Analysis</h4>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h5 className="font-medium text-gray-700 mb-2">Prompt Characteristics</h5>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Length: {parsePromptContent(llmRequest.prompt).length} characters</li>
                      <li>• Tokens: {stats.promptTokens}</li>
                      <li>• Lines: {parsePromptContent(llmRequest.prompt).split('\n').length}</li>
                      <li>• Model: {llmRequest.model}</li>
                    </ul>
                  </div>

                  <div>
                    <h5 className="font-medium text-gray-700 mb-2">Response Characteristics</h5>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Length: {llmRequest.response.length} characters</li>
                      <li>• Tokens: {stats.responseTokens}</li>
                      <li>• Processing Time: {stats.processingTime}ms</li>
                      <li>• Cost: {stats.totalTokens > 0 ? `$${stats.cost.toFixed(6)}` : 'N/A'}</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div>
                <h5 className="font-medium text-gray-700 mb-2">Efficiency Metrics</h5>
                <div className="bg-gray-50 p-4 rounded-md">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="font-medium">Token Efficiency:</span>
                      <div className="text-gray-600">
                        {stats.totalTokens > 0 ?
                          `${((stats.responseTokens / stats.totalTokens) * 100).toFixed(1)}% response` :
                          'N/A'
                        }
                      </div>
                    </div>
                    <div>
                      <span className="font-medium">Speed:</span>
                      <div className="text-gray-600">
                        {stats.tokensPerSecond > 0 ? `${stats.tokensPerSecond} tokens/sec` : 'N/A'}
                      </div>
                    </div>
                    <div>
                      <span className="font-medium">Cost per Token:</span>
                      <div className="text-gray-600">
                        {stats.totalTokens > 0 ?
                          `$${(stats.cost / stats.totalTokens).toFixed(8)}` :
                          'N/A'
                        }
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}